import { OrderStatusEnum } from '#layers/commerce/app/runtime/enums/orders';
import { ProductStatusEnum } from '#layers/commerce/app/runtime/enums/products';

function daysAgoIso(days: number, hours = 0, minutes = 0)
{
    const date = new Date();

    date.setDate(date.getDate() - days);
    date.setHours(date.getHours() - hours, date.getMinutes() - minutes, 0, 0);

    return date.toISOString();
}

function buildSessionsChart(currentValues: number[], previousValues: number[])
{
    const start = new Date();

    start.setDate(start.getDate() - currentValues.length + 1);

    return currentValues.map((currentSessions, index) =>
    {
        const date = new Date(start);

        date.setDate(start.getDate() + index);

        const dateKey = date.toISOString().slice(0, 10);

        return {
            dateFrom: dateKey,
            dateTo: null,
            currentSessions,
            previousSessions: previousValues[index]
        };
    });
}

function buildSalesChart(currentValues: number[], previousValues: number[])
{
    const start = new Date();

    start.setDate(start.getDate() - currentValues.length + 1);

    return currentValues.map((currentSales, index) =>
    {
        const date = new Date(start);

        date.setDate(start.getDate() + index);

        const dateKey = date.toISOString().slice(0, 10);

        return {
            dateFrom: dateKey,
            dateTo: null,
            currentSales,
            previousSales: previousValues[index]
        };
    });
}

function productItem(id: number, name: string, count: number, extra: Record<string, unknown> = {})
{
    return {
        id,
        name,
        photo: null,
        status: ProductStatusEnum.Published,
        count,
        minStorageCount: 10,
        storageCount: 25,
        ...extra
    };
}

// Wykresy liniowe — 15 punktów jak na mockupach (oś X co 2. dzień → 8 etykiet)
const SESSIONS_CURRENT = [
    1780, 2040, 1920, 2360, 2180, 2580, 2440, 2860, 2720, 3080, 2840, 2980, 2560, 2740, 2080
];

const SESSIONS_PREVIOUS = [
    1560, 1680, 1620, 1760, 1700, 1820, 1780, 1900, 1860, 1980, 1920, 1960, 1840, 1880, 1660
];

const SALES_CURRENT = [
    7800, 9200, 10500, 8900, 11200, 12600, 10900, 13400, 12000, 14800, 13200, 14200, 11600, 12900, 10200
];

const SALES_PREVIOUS = [
    6800, 7800, 8600, 7400, 9200, 10400, 9000, 11000, 9800, 12200, 10800, 11600, 9600, 10600, 8600
];

export const dashboardMockupData = {
    ordersTotalCount: {
        value: 1342,
        periodChangePercentage: 8.1
    },
    ordersTotalValue: {
        value: 248730,
        periodChangePercentage: 12.4,
        currencySymbol: 'PLN'
    },
    ordersAverageValue: {
        value: 185.32,
        periodChangePercentage: 3.9,
        currencySymbol: 'PLN'
    },
    sessionsCount: {
        value: 48230,
        periodChange: -2.1
    },
    abandonedCarts: {
        value: 65.1,
        startedCheckouts: 3120
    },
    salesStructure: [
        { key: 'gross', label: '[[[Sprzedaż brutto]]]', value: 284120, variant: 'emphasis' },
        { key: 'discounts', label: '[[[Rabaty]]]', value: -18240, variant: 'negative' },
        { key: 'returns', label: '[[[Zwroty]]]', value: -14320, variant: 'negative' },
        { key: 'net', label: '[[[Sprzedaż netto]]]', value: 251560, variant: 'emphasis' },
        { key: 'shipping', label: '[[[Koszty wysyłki]]]', value: 8420, variant: 'positive' },
        { key: 'returnFees', label: '[[[Opłaty za zwroty]]]', value: 1240, variant: 'positive' },
        { key: 'taxes', label: '[[[Podatki]]]', value: 57860, variant: 'positive' },
        { key: 'total', label: '[[[Sprzedaż całkowita]]]', value: 319080, variant: 'total' }
    ],
    purchaseFunnel: [
        { key: 'sessions', label: '[[[Sesje]]]', value: 48230, conversionRate: null, barColor: '#ff9800' },
        { key: 'productViews', label: '[[[Wyświetlenia produktu]]]', value: 31420, conversionRate: 65.1, barColor: '#ff9800' },
        { key: 'addToCart', label: '[[[Dodano do koszyka]]]', value: 8940, conversionRate: 28.5, barColor: '#ff9800' },
        { key: 'checkout', label: '[[[Rozpoczęto checkout]]]', value: 3120, conversionRate: 34.9, barColor: '#ff9800' },
        { key: 'purchase', label: '[[[Zakup]]]', value: 1342, conversionRate: 43.0, barColor: '#50b888' }
    ],
    cohortRetention: {
        periodLabel: '[[[Miesięcznie]]]',
        monthHeaders: ['M0', 'M1', 'M2', 'M3', 'M4', 'M5'],
        rows: [
            { cohort: 'Sty 2026', clients: 412, retention: [100, 44, 31, 22, 16, 11] },
            { cohort: 'Lut 2026', clients: 384, retention: [100, 41, 29, 21, 15, null] },
            { cohort: 'Mar 2026', clients: 368, retention: [100, 39, 28, 20, 14, null] },
            { cohort: 'Kwi 2026', clients: 351, retention: [100, 37, 26, 18, null, null] },
            { cohort: 'Maj 2026', clients: 329, retention: [100, 35, 24, null, null, null] },
            { cohort: 'Cze 2026', clients: 302, retention: [100, null, null, null, null, null] }
        ]
    },
    complaintRate: {
        value: 2.4,
        description: '[[[reklamacje / zamówienia]]]',
        backgroundColor: '#7d5fce'
    },
    returnRate: {
        value: 3.8,
        description: '[[[zwroty / zamówienia]]]',
        backgroundColor: '#5b9bd5'
    },
    complaintReasons: [
        { label: '[[[Uszkodzenie podczas transportu]]]', value: 38 },
        { label: '[[[Wada produkcyjna]]]', value: 27 },
        { label: '[[[Niezgodność z opisem]]]', value: 19 },
        { label: '[[[Brakujące elementy]]]', value: 14 },
        { label: '[[[Inny]]]', value: 8 }
    ],
    returnReasons: [
        { label: '[[[Niewłaściwy rozmiar]]]', value: 52 },
        { label: '[[[Produkt nie spełnia oczekiwań]]]', value: 34 },
        { label: '[[[Zmiana decyzji]]]', value: 28 },
        { label: '[[[Lepsza oferta gdzie indziej]]]', value: 11 },
        { label: '[[[Inny]]]', value: 6 }
    ],
    complaintHandlingTime: {
        value: 3.2,
        periodChange: -0.4
    },
    returnHandlingTime: {
        value: 2.1,
        periodChange: -0.2
    },
    conversionRate: {
        value: 2.78,
        periodChange: 0.3
    },
    complaintsTotalCount: {
        value: 106,
        periodChangePercentage: 5.8
    },
    complaintsPendingCount: {
        value: 18,
        periodChangePercentage: null
    },
    returnsTotalCount: {
        value: 131,
        periodChangePercentage: 7.2
    },
    returnsPendingCount: {
        value: 22,
        periodChangePercentage: null
    },
    sessionsChart: buildSessionsChart(SESSIONS_CURRENT, SESSIONS_PREVIOUS),
    salesStatistics: buildSalesChart(SALES_CURRENT, SALES_PREVIOUS),
    sessionsByDevice: [
        { name: 'mobile', value: 26840 },
        { name: 'desktop', value: 14820 },
        { name: 'tablet', value: 6570 }
    ],
    newVsReturningUsers: [
        { name: 'nowi', value: 14280 },
        { name: 'powracający', value: 22140 }
    ],
    ordersByMedium: [
        { name: 'Sklep online', count: 184320 },
        { name: 'Allegro', count: 42180 },
        { name: 'Punkt stacjonarny', count: 18420 },
        { name: 'Facebook Shop', count: 8240 },
        { name: 'Instagram Shop', count: 4710 }
    ],
    ordersBySource: [
        { name: 'Bezpośrednie', count: 32140 },
        { name: 'Google Ads', count: 28420 },
        { name: 'Organiczne', count: 24180 },
        { name: 'Facebook', count: 16840 },
        { name: 'Newsletter', count: 14260 },
        { name: 'Allegro', count: 11820 }
    ],
    ordersByCampaign: [
        { name: 'Wiosna 2026', count: 186 },
        { name: 'Remarketing', count: 142 },
        { name: 'Black Week', count: 98 }
    ],
    ordersByCities: [
        { city: 'Mazowieckie', value: 18000 },
        { city: 'Małopolskie', value: 12500 },
        { city: 'Śląskie', value: 11000 },
        { city: 'Wielkopolskie', value: 9500 },
        { city: 'Dolnośląskie', value: 8500 },
        { city: 'Pomorskie', value: 7000 },
        { city: 'Łódzkie', value: 6000 },
        { city: 'Lubelskie', value: 4500 }
    ],
    ordersPaymentsCount: [
        { id: 1, name: 'BLIK', count: 470 },
        { id: 2, name: 'Karta', count: 362 },
        { id: 3, name: 'Przelewy24', count: 322 },
        { id: 4, name: 'PayPo (raty)', count: 161 },
        { id: 5, name: 'Za pobraniem', count: 94 }
    ],
    ordersDeliveriesCount: [
        { id: 1, name: 'InPost Paczkomat', count: 612 },
        { id: 2, name: 'DPD Kurier', count: 284 },
        { id: 3, name: 'DHL Kurier', count: 198 },
        { id: 4, name: 'Poczta Polska', count: 142 },
        { id: 5, name: 'Odbiór osobisty', count: 110 }
    ],
    latestOrders: [
        {
            id: 18482,
            orderNumber: '#18482',
            givenName: 'Anna',
            surname: 'Kowalska',
            status: OrderStatusEnum.Paid,
            totalPriceGross: 489.00,
            currency: 'PLN',
            dateCreatedUtc: daysAgoIso(0, 0, 2)
        },
        {
            id: 18481,
            orderNumber: '#18481',
            givenName: 'Piotr',
            surname: 'Nowak',
            status: OrderStatusEnum.Sent,
            totalPriceGross: 1240.50,
            currency: 'PLN',
            dateCreatedUtc: daysAgoIso(0, 0, 14)
        },
        {
            id: 18480,
            orderNumber: '#18480',
            givenName: 'Magdalena',
            surname: 'Lis',
            status: OrderStatusEnum.New,
            totalPriceGross: 189.99,
            currency: 'PLN',
            dateCreatedUtc: daysAgoIso(0, 0, 27)
        },
        {
            id: 18479,
            orderNumber: '#18479',
            givenName: 'Tomasz',
            surname: 'Wiśniewski',
            status: OrderStatusEnum.Paid,
            totalPriceGross: 3420.00,
            currency: 'PLN',
            dateCreatedUtc: daysAgoIso(0, 0, 41)
        },
        {
            id: 18478,
            orderNumber: '#18478',
            givenName: 'Karolina',
            surname: 'Mazur',
            status: OrderStatusEnum.Cancelled,
            totalPriceGross: 78.40,
            currency: 'PLN',
            dateCreatedUtc: daysAgoIso(0, 1, 0)
        },
        {
            id: 18477,
            orderNumber: '#18477',
            givenName: 'Jakub',
            surname: 'Wójcik',
            status: OrderStatusEnum.Sent,
            totalPriceGross: 612.30,
            currency: 'PLN',
            dateCreatedUtc: daysAgoIso(0, 1, 12)
        }
    ],
    bestPayingCustomers: [
        { id: 1, givenName: 'Anna', surname: 'Kowalska', count: 24, totalOrdersPriceGross: 18420.50 },
        { id: 2, givenName: 'Piotr', surname: 'Nowak', count: 19, totalOrdersPriceGross: 14180.00 },
        { id: 3, givenName: 'Magdalena', surname: 'Lis', count: 17, totalOrdersPriceGross: 11240.30 },
        { id: 4, givenName: 'Tomasz', surname: 'Wiśniewski', count: 14, totalOrdersPriceGross: 9870.90 },
        { id: 5, givenName: 'Karolina', surname: 'Mazur', count: 12, totalOrdersPriceGross: 8540.00 }
    ],
    promotionCodesEffectiveness: [
        { code: 'WIOSNA20', usesCount: 248, totalOrdersPriceGross: 32410, avarageOrdersPriceGross: 130.68 },
        { code: 'FREESHIP', usesCount: 184, totalOrdersPriceGross: 21870, avarageOrdersPriceGross: 118.86 },
        { code: 'NEWSLETTER10', usesCount: 142, totalOrdersPriceGross: 15240, avarageOrdersPriceGross: 107.32 },
        { code: 'VIP15', usesCount: 67, totalOrdersPriceGross: 18920, avarageOrdersPriceGross: 282.38 }
    ],
    topSellingProducts: [
        productItem(1, 'Słuchawki Panasonic RP-HJE125E-R', 73),
        productItem(2, 'Zestaw AGD Bosch HBA534BW0', 57),
        productItem(3, 'Kubek Smart 200ml', 50),
        productItem(4, 'Lizak Aniołek czerwony', 41),
        productItem(5, 'Czekolada Peanut Butter', 38)
    ],
    topSellingVariants: [
        productItem(11, 'Bluza H&M – M / Czarna', 64),
        productItem(12, 'Buty Nike Pegasus – 42 / Białe', 49),
        productItem(13, 'Plecak Eastpak – Szary', 37),
        productItem(14, 'Koszula Reserved – L / Niebieska', 31),
        productItem(15, 'Czapka NewEra – OS / Czarna', 24)
    ],
    mostComplainedProducts: [
        productItem(21, 'Buty sportowe Adidas Runfalcon', 42),
        productItem(22, 'Sukienka letnia Mohito', 31),
        productItem(23, 'Smartwatch Xiaomi Mi Band 8', 24),
        productItem(24, 'Plecak szkolny CoolPack', 18)
    ],
    mostAbandonedProducts: [
        productItem(31, 'Konsola PlayStation 5', 184),
        productItem(32, 'Telewizor LG OLED 55', 142),
        productItem(33, 'Laptop Lenovo IdeaPad 3', 121),
        productItem(34, 'Robot kuchenny Bosch', 98)
    ],
    lowStockProducts: {
        totalRows: 5,
        items: [
            productItem(41, 'Słuchawki Panasonic RP-HJE125E-R', 0, { symbol: 'PAN-RP-125', minStorageCount: 10, storageCount: 3 }),
            productItem(42, 'Czekolada Peanut Butter 100g', 0, { symbol: 'CZK-PB-100', minStorageCount: 25, storageCount: 5 }),
            productItem(43, 'Kubek Smart 200ml – Czarny', 0, { symbol: 'KS-200-BK', minStorageCount: 15, storageCount: 2 }),
            productItem(44, 'Lizak Aniołek czerwony', 0, { symbol: 'LIZ-AN-RD', minStorageCount: 30, storageCount: 8 }),
            productItem(45, 'Mleko UHT 3,2% 1L', 0, { symbol: 'MLK-UHT-1L', minStorageCount: 20, storageCount: 6 })
        ],
        meta: null
    }
};
